import { Card, Col, message, Row, Tag, Typography } from "antd"
import React, { useEffect } from "react"
import {
  ShoppingOutlined,
  DatabaseOutlined,
  SafetyCertificateOutlined,
  SlidersOutlined,
  RollbackOutlined,
  BuildOutlined,
  SwapOutlined,
  FileTextOutlined,
  AuditOutlined,
  ApartmentOutlined,
  DollarCircleOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  ExportOutlined,
  InboxOutlined,
  FileProtectOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons"
import { useConfig } from "../../contexts/ConfigContext"
import { modules } from "../../config/pages"
import { useRegister } from "../../contexts/RegisterContext"
import { useItems } from "../../hooks/useInventory"
import useAuth from "../../hooks/useAuth"
import { useCompany } from "../../contexts/CompanyContext"
import { TModuleOptions } from "../../models/storageModel"

const { Title, Text, Paragraph } = Typography

interface MovementMeta {
  title?: string
  description: string
  badge: string
  badgeBg: string
  color: string
  bgLight: string
  icon: React.ReactNode
}

const ENTRY_METAS: Record<string, MovementMeta> = {
  PURCHASE_RECEIPT: {
    title: "Entrada por Compra",
    description: "Ingreso de mercancías o materias primas compradas a proveedores, más registro de servicios y costos directos asociados (fletes, mano de obra, descargue).",
    badge: "Factura / Compras",
    badgeBg: "#ecfdf5",
    color: "#059669",
    bgLight: "#d1fae5",
    icon: <ShoppingOutlined />,
  },
  INITIAL_INVENTORY: {
    title: "Inventario Inicial",
    description: "Carga y balance de existencias de arranque al comenzar a operar el sistema o al habilitar una nueva bodega física.",
    badge: "Apertura",
    badgeBg: "#eef2ff",
    color: "#4f46e5",
    bgLight: "#e0e7ff",
    icon: <DatabaseOutlined />,
  },
  PARTNER_CONTRIBUTION: {
    title: "Aporte de Socio",
    description: "Bienes, materias primas o inventario suministrado directamente por los socios de la empresa como aporte de capital o patrimonio.",
    badge: "Patrimonio",
    badgeBg: "#f5f3ff",
    color: "#7c3aed",
    bgLight: "#ede9fe",
    icon: <SafetyCertificateOutlined />,
  },
  ADJUSTMENT: {
    title: "Ajuste de Entrada",
    description: "Incremento manual de existencias derivado de conteos físicos periódicos, sobrantes detectados o auditorías de inventario.",
    badge: "Ajuste (+)",
    badgeBg: "#ecfeff",
    color: "#0891b2",
    bgLight: "#cffafe",
    icon: <SlidersOutlined />,
  },
  RETURN_IN: {
    title: "Devolución de Cliente",
    description: "Reingreso al almacén de productos o mercancías devueltas por un cliente tras una venta o despacho previo.",
    badge: "Devolución",
    badgeBg: "#fffbeb",
    color: "#d97706",
    bgLight: "#fef3c7",
    icon: <RollbackOutlined />,
  },
  PRODUCTION_ENTRY: {
    title: "Entrada por Producción",
    description: "Ingreso a bodega de productos terminados o semielaborados fabricados a partir de órdenes de manufactura internas.",
    badge: "Manufactura",
    badgeBg: "#f0f9ff",
    color: "#0284c7",
    bgLight: "#e0f2fe",
    icon: <BuildOutlined />,
  },
  TRANSFER: {
    title: "Traslado - Entrada",
    description: "Recepción y acuse de recibo de mercancías despachadas desde otra sucursal, sede o almacén de la empresa.",
    badge: "Traslado",
    badgeBg: "#f0fdfa",
    color: "#0d9488",
    bgLight: "#ccfbf1",
    icon: <SwapOutlined />,
  },
  PURCHASE_ORDER: {
    title: "Orden de Compra",
    description: "Emisión y formalización de pedidos de compras a proveedores previo a la recepción física de la mercancía.",
    badge: "Orden",
    badgeBg: "#f8fafc",
    color: "#475569",
    bgLight: "#f1f5f9",
    icon: <FileTextOutlined />,
  },
  PURCHASE_REQUEST: {
    title: "Solicitud de Compra",
    description: "Requerimiento interno de suministros o materiales pendiente de cotización y visto bueno por el área de compras.",
    badge: "Solicitud",
    badgeBg: "#f8fafc",
    color: "#64748b",
    bgLight: "#f1f5f9",
    icon: <AuditOutlined />,
  },
  CONVERSION: {
    title: "Fraccionamiento / Conversión",
    description: "Entrada de unidades fraccionadas o transformadas a partir del despiece o conversión de ítems mayores.",
    badge: "Transformación",
    badgeBg: "#faf5ff",
    color: "#9333ea",
    bgLight: "#f3e8ff",
    icon: <ApartmentOutlined />,
  },
}

const EXIT_METAS: Record<string, MovementMeta> = {
  SALE_INVOICE: {
    title: "Factura / Salida por Venta",
    description: "Despacho y rebaja definitiva de existencias en almacén por ventas a clientes, con soporte de factura electrónica o remisión.",
    badge: "Ventas",
    badgeBg: "#ecfdf5",
    color: "#059669",
    bgLight: "#d1fae5",
    icon: <DollarCircleOutlined />,
  },
  WASTE: {
    title: "Bajas / Pérdidas de Inventario",
    description: "Descargo definitivo de artículos por vencimiento, rotura, merma no recuperable, daño físico o avería comprobada.",
    badge: "Bajas / Mermas",
    badgeBg: "#fef2f2",
    color: "#e11d48",
    bgLight: "#ffe4e6",
    icon: <DeleteOutlined />,
  },
  PRODUCTION_EXIT: {
    title: "Salida a Producción",
    description: "Despacho y consumo de materias primas, piezas o insumos destinados a órdenes de trabajo o manufactura interna.",
    badge: "Consumo",
    badgeBg: "#fffbeb",
    color: "#d97706",
    bgLight: "#fef3c7",
    icon: <ExperimentOutlined />,
  },
  RETURN_OUT: {
    title: "Devolución a Proveedor",
    description: "Salida y despacho de mercancía devuelta a proveedores por garantías, defectos de fábrica o inconformidad de pedido.",
    badge: "Garantía",
    badgeBg: "#eef2ff",
    color: "#4f46e5",
    bgLight: "#e0e7ff",
    icon: <ExportOutlined />,
  },
  ADJUSTMENT: {
    title: "Ajuste de Salida",
    description: "Disminución manual de existencias tras detectar faltantes en conteos de inventario físico, revisiones o auditorías.",
    badge: "Ajuste (-)",
    badgeBg: "#f8fafc",
    color: "#475569",
    bgLight: "#f1f5f9",
    icon: <SlidersOutlined />,
  },
  TRANSFER: {
    title: "Traslado - Salida",
    description: "Despacho y salida de mercancías desde esta bodega con destino hacia otra sede, sucursal o almacén de la empresa.",
    badge: "Traslado",
    badgeBg: "#f0fdfa",
    color: "#0d9488",
    bgLight: "#ccfbf1",
    icon: <SwapOutlined />,
  },
  QUOTATION: {
    title: "Cotización Comercial",
    description: "Generación de propuesta o presupuesto comercial para clientes, sin reservar ni descontar existencias físicas en bodega.",
    badge: "Cotización",
    badgeBg: "#f5f3ff",
    color: "#7c3aed",
    bgLight: "#ede9fe",
    icon: <FileProtectOutlined />,
  },
}

function getMovementMeta(key: string, isExit: boolean): MovementMeta {
  if (isExit && EXIT_METAS[key]) {
    return EXIT_METAS[key]
  }
  if (!isExit && ENTRY_METAS[key]) {
    return ENTRY_METAS[key]
  }
  if (EXIT_METAS[key]) return EXIT_METAS[key]
  if (ENTRY_METAS[key]) return ENTRY_METAS[key]

  return isExit
    ? {
        description: "Registro de operación de salida o despacho de existencias del inventario.",
        badge: "Salida",
        badgeBg: "#eff6ff",
        color: "#2563eb",
        bgLight: "#dbeafe",
        icon: <ExportOutlined />,
      }
    : {
        description: "Registro de operación de entrada o incremento de existencias en el inventario.",
        badge: "Ingreso",
        badgeBg: "#ecfdf5",
        color: "#059669",
        bgLight: "#d1fae5",
        icon: <InboxOutlined />,
      }
}

function getTypes(module: string, movementTypesEntry: TModuleOptions[], movementTypesExit: TModuleOptions[]) {
  switch (module) {
    case modules.RegisterEntry:
      return movementTypesEntry
    case modules.PendingEntriesList:
      return movementTypesEntry
    case modules.MovementList:
      return [...movementTypesEntry, ...movementTypesExit]
    default:
      return movementTypesExit
  }
}

function SelectMovementType() {
  const { config } = useConfig()
  const { company } = useCompany()
  const { register, updateRegister } = useRegister()
  const mod = config.module
  const { tokenData } = useAuth.useToken("authCsrfToken", config.session, config.key)
  const { data: itData } = useItems(tokenData, {
    company: company.current?.uid,
    module: config.module,
  })

  useEffect(() => {
    if (tokenData && itData?.itemList) {
      updateRegister({ registerList: itData.itemList })
    }
  }, [itData])

  const isEntry = mod === modules.RegisterEntry || mod === modules.PendingEntriesList
  const isExit = mod === modules.RegisterExit || mod === modules.PendingExitsList

  const types = getTypes(
    mod,
    register.movementTypesEntry.filter((m) => m.show === true),
    register.movementTypesExit.filter((m) => m.show === true)
  )

  const handleSelect = (key: string) => {
    if (register.registerList.length === 0) {
      message.info("Recuerde mantener actualizados sus elementos")
    }
    updateRegister({ currentMovementType: key })
  }

  return (
    <div style={{ maxWidth: 1140, margin: "0 auto", padding: "2rem 1rem 3rem" }}>
      <style>{`
        .movement-type-card {
          border-radius: 16px !important;
          border: 1px solid #e2e8f0 !important;
          background: #ffffff !important;
          box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04) !important;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
          cursor: pointer !important;
          height: 100% !important;
        }
        .movement-type-card:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 16px 32px -4px rgba(15, 23, 42, 0.1), 0 4px 12px rgba(15, 23, 42, 0.04) !important;
          border-color: var(--card-accent, #2563eb) !important;
        }
        .movement-type-card:hover .action-arrow {
          transform: translateX(4px);
        }
        .action-arrow {
          transition: transform 0.2s ease;
        }
      `}</style>

      <div style={{ textAlign: "center", marginBottom: "2.25rem" }}>
        <div style={{ marginBottom: "0.6rem" }}>
          <Tag
            style={{
              borderRadius: "20px",
              padding: "4px 14px",
              fontWeight: 600,
              fontSize: "12px",
              border: "none",
              backgroundColor: isEntry ? "#ecfdf5" : isExit ? "#eff6ff" : "#f1f5f9",
              color: isEntry ? "#059669" : isExit ? "#2563eb" : "#475569",
              letterSpacing: "0.5px",
            }}
          >
            {isEntry ? "📥 OPERACIONES DE INGRESO" : isExit ? "📤 OPERACIONES DE SALIDA" : "🔄 MOVIMIENTOS DE ALMACÉN"}
          </Tag>
        </div>
        <Title level={2} style={{ margin: "0 0 0.5rem 0", color: "#0f172a", fontWeight: 700 }}>
          {isEntry
            ? "Seleccione el tipo de ingreso"
            : isExit
            ? "Seleccione el tipo de salida"
            : "Seleccione el tipo de movimiento"}
        </Title>
        <Text style={{ color: "#64748b", fontSize: "0.95rem" }}>
          {isEntry
            ? "Elige la operación que deseas registrar en el almacén o facturación de compras"
            : isExit
            ? "Elige el tipo de movimiento o despacho que deseas efectuar del inventario"
            : "Selecciona el tipo de registro que deseas gestionar en el sistema"}
        </Text>
      </div>

      <Row gutter={[20, 20]} justify="center">
        {types.map((type) => {
          const meta = getMovementMeta(type.key, isExit)
          return (
            <Col xs={24} sm={12} md={8} lg={8} key={type.key} style={{ display: "flex" }}>
              <Card
                hoverable
                className="movement-type-card"
                onClick={() => handleSelect(type.key)}
                style={{
                  width: "100%",
                  "--card-accent": meta.color,
                } as React.CSSProperties}
                styles={{
                  body: {
                    padding: "22px",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    justifyContent: "space-between",
                  },
                }}
              >
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        background: meta.bgLight,
                        color: meta.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "22px",
                        boxShadow: `inset 0 0 0 1px ${meta.color}25`,
                      }}
                    >
                      {meta.icon}
                    </div>
                    <Tag
                      style={{
                        borderRadius: "20px",
                        padding: "2px 10px",
                        fontSize: "11px",
                        fontWeight: 600,
                        border: "none",
                        backgroundColor: meta.badgeBg,
                        color: meta.color,
                        margin: 0,
                      }}
                    >
                      {meta.badge}
                    </Tag>
                  </div>

                  <div style={{ marginBottom: "8px" }}>
                    <Text strong style={{ fontSize: "1.08rem", color: "#0f172a", display: "block" }}>
                      {type.string_value || meta.title}
                    </Text>
                  </div>

                  <Paragraph
                    type="secondary"
                    style={{
                      fontSize: "0.84rem",
                      lineHeight: 1.5,
                      color: "#64748b",
                      marginBottom: "16px",
                      minHeight: "44px",
                    }}
                  >
                    {meta.description}
                  </Paragraph>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: "14px",
                    borderTop: "1px solid #f1f5f9",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: meta.color,
                  }}
                >
                  <span>Continuar registro</span>
                  <ArrowRightOutlined className="action-arrow" />
                </div>
              </Card>
            </Col>
          )
        })}
      </Row>
    </div>
  )
}

export default SelectMovementType
